import { useEffect, useState } from "react";

/**
 * The app's one widescreen breakpoint, resolved in JS.
 *
 * **1536px, which is Tailwind's `2xl`.** Stats established it (its handoff said "above ~1500px")
 * and Review joined it in 日本-350, whose handoff says 1600. Alex's call, 16 Aug 2026: reuse the
 * existing line. Two widescreen breakpoints 64px apart is a difference nobody can perceive and
 * everybody has to remember, and neither number changes anything for the widths this app is
 * actually used at — a 3440 ultrawide gives ~1147 at a third and ~2293 at two thirds.
 *
 * **In JS rather than a `2xl:` class because the two arrangements are different *partitions*, not
 * one layout re-flowed.** Stats' `Recall` moves `Lapses` between groups; Review's answer face turns
 * one centred column into two ranged-left ones with the images in a column of their own. No flex
 * direction, wrap or `order` turns one nesting into the other, so each is written out and the
 * breakpoint chooses.
 *
 * Crossing it remounts whatever is inside, which is a real cost paid only by somebody dragging a
 * window across 1536px. Surfaces whose two arrangements are the same grouping re-flowed should keep
 * using `2xl:` classes instead.
 */
export const WIDE_MIN_PX = 1536;

export function useWideLayout(): boolean {
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= WIDE_MIN_PX,
  );
  useEffect(() => {
    const measure = () => setWide(window.innerWidth >= WIDE_MIN_PX);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  return wide;
}
